import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const FeaturedCarousel = ({ projects, onViewDetails }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      handleNext();
    }, 5000);

    return () => clearInterval(timer);
  }, [currentIndex]);

  const handleNext = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % projects?.length);
  };

  const handlePrev = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + projects?.length) % projects?.length);
  };

  const variants = {
    enter: (direction) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction) => ({
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  const currentProject = projects?.[currentIndex];

  return (
    <div className="relative bg-card rounded-xl overflow-hidden border border-border shadow-lg mb-6 md:mb-8">
      <div className="relative h-64 md:h-80 lg:h-96 overflow-hidden">
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={currentIndex}
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: 'spring', stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
            className="absolute inset-0"
          >
            <Image
              src={currentProject?.thumbnail}
              alt={currentProject?.thumbnailAlt}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
          </motion.div>
        </AnimatePresence>

        <div className="absolute top-4 left-4 md:top-6 md:left-6">
          <span className="px-3 py-1.5 bg-primary text-primary-foreground text-xs md:text-sm font-semibold rounded-full flex items-center gap-2">
            <Icon name="Star" size={16} color="white" />
            Featured Project
          </span>
        </div>

        <div className="absolute inset-x-0 bottom-0 p-4 md:p-6 lg:p-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-2 text-white">
              {currentProject?.title}
            </h3>
            <p className="text-sm md:text-base text-white/90 mb-4 line-clamp-2">
              {currentProject?.description}
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {currentProject?.technologies?.slice(0, 4)?.map((tech, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-white/20 backdrop-blur-sm text-white text-xs rounded-md"
                >
                  {tech}
                </span>
              ))}
            </div>
            <Button
              variant="default"
              size="sm"
              onClick={() => onViewDetails(currentProject)}
              iconName="ArrowRight"
              iconPosition="right"
            >
              View Details
            </Button>
          </motion.div>
        </div>

        <button
          onClick={handlePrev}
          className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors"
          aria-label="Previous project"
        >
          <Icon name="ChevronLeft" size={24} color="white" />
        </button>

        <button
          onClick={handleNext}
          className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors"
          aria-label="Next project"
        >
          <Icon name="ChevronRight" size={24} color="white" />
        </button>
      </div>
      <div className="flex items-center justify-center gap-2 p-4">
        {projects?.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setDirection(index > currentIndex ? 1 : -1);
              setCurrentIndex(index);
            }}
            className={`h-2 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'w-8 bg-primary' : 'w-2 bg-muted'
            }`}
            aria-label={`Go to project ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default FeaturedCarousel;